import { useState, useEffect, useRef, useCallback } from 'react';
import * as Video from 'twilio-video';
import { getUserDetails } from '../utils';
import { useQueryTwilioVideoToken } from './react-query/twilioVideo';

/**
 * Custom hook for Twilio Video management
 */
const useTwilioVideo = (socket, meetingId, recipientId, onCallEnded) => {
    const userDetails = getUserDetails();
    const userToken = userDetails?.token;

    // React Query for Twilio Video token
    const {
        data: tokenResponse,
        isLoading: tokenLoading,
        error: tokenError,
        refetch: refetchToken
    } = useQueryTwilioVideoToken(meetingId, userToken, null, null);

    // Extract token data from API response
    const tokenData = tokenResponse?.data?.data;

    // Twilio Video states
    const [localStream, setLocalStream] = useState(null);
    const [isCallActive, setIsCallActive] = useState(false);
    const [isAudioEnabled, setIsAudioEnabled] = useState(true);
    const [isVideoEnabled, setIsVideoEnabled] = useState(true);
    const [isRemoteVideoEnabled, setIsRemoteVideoEnabled] = useState(true);
    const [isRemoteAudioEnabled, setIsRemoteAudioEnabled] = useState(true);
    const [hasRemoteParticipant, setHasRemoteParticipant] = useState(false);
    const [error, setError] = useState(null);
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [isScreenSharing, setIsScreenSharing] = useState(false);
    const [isRemoteScreenSharing, setIsRemoteScreenSharing] = useState(false);
    const [isScreenSharingLoading, setIsScreenSharingLoading] = useState(false);
    const [isRemoteScreenSharingLoading, setIsRemoteScreenSharingLoading] = useState(false);
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const [statusMessage, setStatusMessage] = useState(null);
    const [statusMessageType, setStatusMessageType] = useState('info'); // 'info', 'success', 'warning', 'error'
    const [transcript, setTranscript] = useState('');
    const [isConnecting, setIsConnecting] = useState(false);

    // Ensure screen share state is false on mount
    useEffect(() => {
        setIsRemoteScreenSharing(false);
    }, []);

    // Refs
    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const localTracksRef = useRef([]);
    const remoteTracksRef = useRef([]);
    const roomRef = useRef(null);
    const isIntentionalEndRef = useRef(false);
    const screenTrackRef = useRef(null);
    const remoteScreenTrackRef = useRef(null);
    const remoteScreenPublicationRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const cameraTrackRef = useRef(null);
    const audioTrackRef = useRef(null);
    const remoteAudioElementRef = useRef(null);
    const remoteParticipantsRef = useRef(new Map());
    const statusMessageTimeoutRef = useRef(null);
    const recognitionRef = useRef(null);
    const transcriptRef = useRef('');
    const reconnectStartTimeRef = useRef(null);
    const maxReconnectDuration = 120000; // 2 minutes in milliseconds
    const transcriptSaveIntervalRef = useRef(null);
    const lastSavedTranscriptRef = useRef('');

    /**
     * Show status message with auto-dismiss
     */
    const showStatusMessage = useCallback((message, type = 'info', duration = 3000) => {
        // Clear existing timeout
        if (statusMessageTimeoutRef.current) {
            clearTimeout(statusMessageTimeoutRef.current);
        }

        setStatusMessage(message);
        setStatusMessageType(type);

        // Auto-dismiss after duration
        statusMessageTimeoutRef.current = setTimeout(() => {
            setStatusMessage(null);
        }, duration);
    }, []);

    /**
     * Check if a track is a screen share track
     * Can accept either a publication (with trackName) or actual track (with name)
     */
    const isScreenShareTrack = useCallback((track) => {
        if (!track) return false;

        // Check if it's a video track
        if (track.kind !== 'video') return false;

        // Check trackName on publication (for remote track publications) - this is the most reliable for remote tracks
        if (track.trackName === 'screen-share') return true;

        // Check name property on track or publication
        if (track.name === 'screen-share') return true;

        // For publications, check the nested actual track's name
        if (track.track && track.track.name === 'screen-share') return true;

        return false;
    }, []);

    /**
     * Attach local video track to video element
     */
    const attachLocalVideo = useCallback(() => {
        if (cameraTrackRef.current && localVideoRef.current) {

            try {
                // Check if track has detach and attach methods
                if (typeof cameraTrackRef.current.detach !== 'function' || typeof cameraTrackRef.current.attach !== 'function') {

                    return;
                }

                // Detach first to avoid duplicates
                cameraTrackRef.current.detach();
                // Attach the track
                cameraTrackRef.current.attach(localVideoRef.current);

                // Force video element to load
                if (localVideoRef.current) {
                    localVideoRef.current.load();
                }
            } catch (err) {
                // Error handled silently
            }
        }
    }, []);

    /**
     * Attach remote video track to video element
     * Can accept either a publication object or actual track
     */
    const attachRemoteVideo = useCallback((track) => {
        if (!track) return;

        // Handle track publications - get the actual track
        const actualTrack = track.track || track;

        // Check if it's a video track
        if (actualTrack.kind !== 'video') return;

        if (!remoteVideoRef.current) return;

        try {
            const remoteVideoElement = remoteVideoRef.current;

            // Pass original track (publication) to check trackName, or actualTrack if it's already the track
            const trackToCheck = track.track ? track : actualTrack;
            const isScreenShare = isScreenShareTrack(trackToCheck);

            // Check if track has detach and attach methods
            if (typeof actualTrack.detach !== 'function' || typeof actualTrack.attach !== 'function') {
                return;
            }

            // Detach any existing video tracks first (prioritize screen share)
            const existingVideoTracks = remoteTracksRef.current.filter(t => {
                const existingActualTrack = t.track || t;
                return existingActualTrack.kind === 'video';
            });
            existingVideoTracks.forEach(t => {
                const existingActualTrack = t.track || t;
                // If we're attaching a screen share, detach camera tracks
                // If we're attaching a camera track and screen share exists, don't detach
                // Pass the original track (publication) to check trackName
                const isAttachingScreenShare = isScreenShareTrack(track);
                // For existing tracks, we need to find the original publication or check the track name
                const existingTrack = remoteTracksRef.current.find(rt => {
                    const rtActual = rt.track || rt;
                    return rtActual === existingActualTrack;
                });
                const isExistingScreenShare = existingTrack ? isScreenShareTrack(existingTrack) : isScreenShareTrack(existingActualTrack);

                if (existingActualTrack !== actualTrack && typeof existingActualTrack.detach === 'function') {
                    if (isAttachingScreenShare && !isExistingScreenShare) {
                        // Detach camera when attaching screen share
                        existingActualTrack.detach();
                    } else if (!isAttachingScreenShare && isExistingScreenShare) {
                        // Don't detach screen share when attaching camera
                    } else if (!isAttachingScreenShare && !isExistingScreenShare) {
                        // Detach other camera tracks
                        existingActualTrack.detach();
                    }
                }
            });

            // Detach the track from any existing element first
            actualTrack.detach();

            // Ensure track is enabled before attaching - CRITICAL for screen share
            if (!actualTrack.isEnabled) {
                actualTrack.enable();
            }

            // For screen share, ensure track is definitely enabled
            if (isScreenShare && !actualTrack.isEnabled) {
                // Force enable if still disabled
                try {
                    actualTrack.enable();
                } catch (enableErr) {
                    // Error handled silently
                }
            }

            // For screen share, ensure the video element is ready BEFORE attaching
            if (isScreenShare) {
                // Ensure video element attributes are set for screen share
                remoteVideoElement.setAttribute('autoplay', 'true');
                remoteVideoElement.setAttribute('playsinline', 'true');
                remoteVideoElement.muted = false; // Screen share should have audio if available
                remoteVideoElement.style.display = 'block';
                remoteVideoElement.style.visibility = 'visible';
                remoteVideoElement.style.opacity = '1';
            }

            // Attach the new track to the video element
            actualTrack.attach(remoteVideoElement);

            // Verify attachment for screen share
            if (isScreenShare) {
                // Double-check that the track is attached
                if (remoteVideoElement.srcObject === null && actualTrack.mediaStreamTrack) {
                    // Fallback: set srcObject directly if attach didn't work
                    remoteVideoElement.srcObject = new MediaStream([actualTrack.mediaStreamTrack]);
                }
            }

            // Force video element to load and play
            remoteVideoElement.load();

            // Ensure video element plays - especially important for screen share
            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
                const playPromise = remoteVideoElement.play();
                if (playPromise !== undefined) {
                    playPromise.then(() => {
                        // Video started playing successfully
                        if (isScreenShare) {
                            setIsRemoteScreenSharingLoading(false);
                        }
                    }).catch(() => {
                        // Error handled silently
                        // Try again after a short delay for screen share
                        if (isScreenShare) {
                            setTimeout(() => {
                                remoteVideoElement.play().catch(() => {
                                    // Error handled silently
                                });
                            }, 500);
                        }
                    });
                }
            });

            // For screen share, wait for video to load before clearing loading state
            if (isScreenShare) {
                // Wait for video to load before clearing loading state
                const handleLoadedData = () => {
                    setIsRemoteScreenSharingLoading(false);
                    remoteVideoElement.removeEventListener('loadeddata', handleLoadedData);
                };
                const handlePlaying = () => {
                    setIsRemoteScreenSharingLoading(false);
                    remoteVideoElement.removeEventListener('playing', handlePlaying);
                };
                const handleCanPlay = () => {
                    setIsRemoteScreenSharingLoading(false);
                    remoteVideoElement.removeEventListener('canplay', handleCanPlay);
                };

                remoteVideoElement.addEventListener('loadeddata', handleLoadedData);
                remoteVideoElement.addEventListener('playing', handlePlaying);
                remoteVideoElement.addEventListener('canplay', handleCanPlay);

                // Fallback: clear loading after 3 seconds even if events don't fire
                setTimeout(() => {
                    setIsRemoteScreenSharingLoading(false);
                    remoteVideoElement.removeEventListener('loadeddata', handleLoadedData);
                    remoteVideoElement.removeEventListener('playing', handlePlaying);
                    remoteVideoElement.removeEventListener('canplay', handleCanPlay);
                }, 3000);

                // Force play again after a short delay to ensure it starts
                setTimeout(() => {
                    if (remoteVideoElement.paused) {
                        remoteVideoElement.play().catch(() => {
                            // Error handled silently
                        });
                    }
                }, 500);
            } else {
                // For non-screen-share tracks, ensure video element is visible
                remoteVideoElement.style.display = 'block';
                remoteVideoElement.style.visibility = 'visible';
            }

        } catch (err) {
            // Error handled silently
        }
    }, [isScreenShareTrack]);

    /**
     * Initialize local media stream using Twilio Video SDK
     */
    const initLocalStream = useCallback(async () => {
        try {

            const localTracks = await Video.createLocalTracks({
                audio: true,
                video: {
                    width: 1280,
                    height: 720,
                    facingMode: 'user'
                }
            });

            localTracksRef.current = localTracks;

            // Store camera and audio track refs for easy access
            const videoTrack = localTracks.find(track => track.kind === 'video');
            const audioTrack = localTracks.find(track => track.kind === 'audio');

            if (videoTrack) {
                cameraTrackRef.current = videoTrack;
                setIsVideoEnabled(videoTrack.isEnabled);

                // Listen to track enabled/disabled events
                videoTrack.on('enabled', () => {

                    setIsVideoEnabled(true);
                    // Re-attach to video element
                    setTimeout(() => attachLocalVideo(), 50);
                });

                videoTrack.on('disabled', () => {

                    setIsVideoEnabled(false);
                });
            }

            if (audioTrack) {
                audioTrackRef.current = audioTrack;
                setIsAudioEnabled(audioTrack.isEnabled);

                // Listen to track enabled/disabled events
                audioTrack.on('enabled', () => {

                    setIsAudioEnabled(true);
                });

                audioTrack.on('disabled', () => {

                    setIsAudioEnabled(false);
                });
            }

            setLocalStream(localTracks);

            return localTracks;
        } catch (err) {

            const errorMessage = err.name === 'NotAllowedError'
                ? 'Camera and microphone access denied. Please allow access and try again.'
                : err.name === 'NotFoundError'
                    ? 'Camera or microphone not found. Please check your devices.'
                    : 'Failed to access camera/microphone. Please check your devices and permissions.';

            setError(errorMessage);
            throw err;
        }
    }, []);

    /**
     * Handle remote track subscription
     */
    const handleRemoteTrack = useCallback((track) => {

        // Handle track publications - get the actual track
        const actualTrack = track.track || track;
        // Pass the original track (publication) to check trackName, function will also check actualTrack.name
        const isScreenShare = isScreenShareTrack(track);

        if (actualTrack.kind === 'video') {
            if (isScreenShare) {
                remoteScreenTrackRef.current = actualTrack;
                remoteScreenPublicationRef.current = track; // Store publication for trackName checking
                setIsRemoteScreenSharingLoading(true);
                setIsRemoteScreenSharing(true);
                showStatusMessage('Other participant is sharing screen', 'info', 3000);

                // Attach screen share to remote video element
                // Pass the original track (publication) so attachRemoteVideo can check trackName
                // Use setTimeout to ensure DOM is ready
                setTimeout(() => {
                    if (remoteVideoRef.current && actualTrack) {
                        attachRemoteVideo(track);
                    }
                }, 100);
            } else {
                // Camera track (not screen share)
                // Check if screen share track still exists and is valid
                const hasActiveScreenShare = remoteScreenTrackRef.current &&
                    remoteScreenTrackRef.current.isEnabled &&
                    remoteScreenTrackRef.current.mediaStreamTrack &&
                    remoteScreenTrackRef.current.mediaStreamTrack.readyState === 'live';

                // Clear screen share state if screen share track is no longer valid
                if (isRemoteScreenSharing && !hasActiveScreenShare) {
                    remoteScreenTrackRef.current = null;
                    remoteScreenPublicationRef.current = null;
                    setIsRemoteScreenSharing(false);
                    setIsRemoteScreenSharingLoading(false);
                }

                // Only attach camera if screen share is not active
                if (!isRemoteScreenSharing || !hasActiveScreenShare) {
                    attachRemoteVideo(track);
                }

                // Check the actual track state, not just assume it's enabled
                setIsRemoteVideoEnabled(actualTrack.isEnabled);
            }
            setHasRemoteParticipant(true);
        } else if (actualTrack.kind === 'audio') {

            // Check the actual track state, not just assume it's enabled
            setIsRemoteAudioEnabled(actualTrack.isEnabled);

            let remoteAudioElement = document.getElementById('remote-audio');
            if (!remoteAudioElement) {
                remoteAudioElement = document.createElement('audio');
                remoteAudioElement.id = 'remote-audio';
                remoteAudioElement.autoplay = true;
                document.body.appendChild(remoteAudioElement);
            }

            // Store ref for easy access
            remoteAudioElementRef.current = remoteAudioElement;

            // Attach track to audio element
            actualTrack.attach(remoteAudioElement);

            // Set initial muted state based on track enabled state
            remoteAudioElement.muted = !actualTrack.isEnabled;

        }

        // Store track reference (store the actual track, not the publication)
        if (!remoteTracksRef.current.includes(actualTrack)) {
            remoteTracksRef.current.push(actualTrack);
        }

        // Track enabled/disabled events (listen on the actual track)
        actualTrack.on('enabled', () => {

            if (actualTrack.kind === 'video') {
                // Re-check if this is a screen share (in case track properties changed)
                const isScreenShareNow = isScreenShareTrack(actualTrack);

                if (isScreenShareNow) {

                    remoteScreenTrackRef.current = actualTrack;
                    setIsRemoteScreenSharingLoading(true);
                    setIsRemoteScreenSharing(true);
                    showStatusMessage('Other participant resumed screen sharing', 'info', 3000);
                    setTimeout(() => {
                        attachRemoteVideo(actualTrack);
                        setTimeout(() => {
                            setIsRemoteScreenSharingLoading(false);
                        }, 1000);
                    }, 50);
                } else {
                    // Only clear screen share if we're not currently screen sharing
                    // (screen share should take priority over camera)
                    if (isRemoteScreenSharing && !remoteScreenTrackRef.current) {
                        setIsRemoteScreenSharing(false);
                        setIsRemoteScreenSharingLoading(false);
                    }
                    // Only attach camera if screen share is not active
                    if (!isRemoteScreenSharing || !remoteScreenTrackRef.current) {
                        setTimeout(() => {
                            attachRemoteVideo(actualTrack);
                        }, 50);
                    }
                    setIsRemoteVideoEnabled(true);
                }
            } else if (actualTrack.kind === 'audio') {

                setIsRemoteAudioEnabled(true);
                // Unmute the audio element
                if (remoteAudioElementRef.current) {
                    remoteAudioElementRef.current.muted = false;

                }
            }
        });

        actualTrack.on('disabled', () => {

            if (actualTrack.kind === 'video') {
                if (isScreenShare) {

                    setIsRemoteScreenSharing(false);
                    setIsRemoteScreenSharingLoading(false);
                    showStatusMessage('Other participant paused screen sharing', 'info', 3000);
                } else {
                    setIsRemoteVideoEnabled(false);
                }
            } else if (actualTrack.kind === 'audio') {

                setIsRemoteAudioEnabled(false);
                // Mute the audio element
                if (remoteAudioElementRef.current) {
                    remoteAudioElementRef.current.muted = true;

                }
            }
        });
    }, [attachRemoteVideo, isScreenShareTrack, showStatusMessage]);

    /**
     * Handle remote track unsubscription
     */
    const handleRemoteTrackRemoved = useCallback((track) => {

        // Handle track publications - get the actual track
        const actualTrack = track.track || track;
        // Pass original track (publication) to check trackName
        const isScreenShare = isScreenShareTrack(track);

        if (actualTrack.kind === 'video') {
            if (isScreenShare) {
                setIsRemoteScreenSharing(false);
                setIsRemoteScreenSharingLoading(false);
                remoteScreenTrackRef.current = null;
                remoteScreenPublicationRef.current = null;
                showStatusMessage('Other participant stopped screen sharing', 'info', 3000);

                // After screen share stops, check if there's a camera track to show
                // The camera track should be re-published by the remote participant
                setTimeout(() => {
                    // Look for camera track in remote tracks
                    const remoteParticipant = Array.from(remoteParticipantsRef.current.values())[0];
                    if (remoteParticipant) {
                        remoteParticipant.videoTracks.forEach((publication) => {
                            const { track } = publication;
                            if (track && track.kind === 'video' && !isScreenShareTrack(publication)) {
                                // Found a camera track, attach it
                                attachRemoteVideo(publication);
                                setIsRemoteVideoEnabled(track.isEnabled);
                            }
                        });
                    }
                }, 300);
            } else {
                setIsRemoteVideoEnabled(false);
            }
        }

        // Remove from refs and detach
        remoteTracksRef.current = remoteTracksRef.current.filter(t => {
            const existingActualTrack = t.track || t;
            return existingActualTrack !== actualTrack;
        });

        if (typeof actualTrack.detach === 'function') {
            actualTrack.detach();
        }

        // Remove event listeners
        if (typeof actualTrack.removeAllListeners === 'function') {
            actualTrack.removeAllListeners();
        }
    }, [isScreenShareTrack, showStatusMessage]);

    /**
     * Handle participant connected
     */
    const handleParticipantConnected = useCallback((participant) => {

        setHasRemoteParticipant(true);
        setIsReconnecting(false);

        // Reset screen share state when new participant joins
        setIsRemoteScreenSharing(false);
        remoteScreenTrackRef.current = null;

        // Show user-friendly message with participant name
        // const participantName = participant.identity || 'Someone';
        // showStatusMessage(`${participantName} joined the meeting`, 'success', 4000);

        // Store participant reference
        remoteParticipantsRef.current.set(participant.sid, participant);

        // Subscribe to existing tracks and check their initial state
        participant.tracks.forEach(publication => {

            if (publication.isSubscribed && publication.track) {
                // For existing tracks, be more conservative about screen share detection
                const actualTrack = publication.track.track || publication.track;

                // Only process as screen share if we have strong evidence
                // Pass original publication to check trackName
                const isScreenShare = isScreenShareTrack(publication);

                if (actualTrack.kind === 'video') {
                    if (isScreenShare) {

                        remoteScreenTrackRef.current = actualTrack;
                        setIsRemoteScreenSharingLoading(true);
                        setIsRemoteScreenSharing(true);
                        attachRemoteVideo(actualTrack);
                        setTimeout(() => {
                            setIsRemoteScreenSharingLoading(false);
                        }, 1000);
                    } else {

                        attachRemoteVideo(actualTrack);
                        setIsRemoteVideoEnabled(actualTrack.isEnabled);
                    }
                } else if (actualTrack.kind === 'audio') {

                    setIsRemoteAudioEnabled(actualTrack.isEnabled);
                    // Set initial muted state
                    if (remoteAudioElementRef.current) {
                        remoteAudioElementRef.current.muted = !actualTrack.isEnabled;

                    }
                }
            }
        });

        // Subscribe to future tracks
        participant.on('trackSubscribed', (track) => {

            // Smart fallback: Only apply screen share detection if we have strong indicators
            if (track.kind === 'video') {
                const actualTrack = track.track || track;

                // Check if this track has screen share characteristics
                const hasScreenShareName = actualTrack.name && (
                    actualTrack.name.toLowerCase().includes('screen') ||
                    actualTrack.name.toLowerCase().includes('display') ||
                    actualTrack.name.toLowerCase().includes('desktop')
                );

                // Check if we already have a camera track and this might be screen share
                const hasCameraTrack = remoteTracksRef.current.some(t => {
                    const existingTrack = t.track || t;
                    return existingTrack.kind === 'video' && !isScreenShareTrack(existingTrack);
                });

                // Only apply fallback if we have screen share name indicators OR if we have a camera track
                if (hasScreenShareName || (hasCameraTrack && remoteTracksRef.current.length > 0)) {

                    actualTrack._forceScreenShare = true;
                }
            }

            handleRemoteTrack(track);
        });

        participant.on('trackUnsubscribed', (track) => {
            handleRemoteTrackRemoved(track);
        });

        participant.on('trackEnabled', (track) => {

            const actualTrack = track.track || track;
            // Pass original track (publication) to check trackName
            const isScreenShare = isScreenShareTrack(track);

            if (track.kind === 'video') {
                if (isScreenShare) {

                    setIsRemoteScreenSharingLoading(true);
                    setIsRemoteScreenSharing(true);
                    showStatusMessage('Other participant resumed screen sharing', 'info', 3000);
                    setTimeout(() => {
                        attachRemoteVideo(actualTrack);
                        setTimeout(() => {
                            setIsRemoteScreenSharingLoading(false);
                        }, 1000);
                    }, 50);
                } else {

                    // If we were showing screen share, clear it
                    if (isRemoteScreenSharing) {
                        setIsRemoteScreenSharing(false);
                        setIsRemoteScreenSharingLoading(false);
                    }
                    showStatusMessage('Other participant turned on camera', 'info', 3000);
                    setTimeout(() => {
                        attachRemoteVideo(actualTrack);
                    }, 50);
                    setIsRemoteVideoEnabled(true);
                }
            } else if (track.kind === 'audio') {

                showStatusMessage('Other participant turned on microphone', 'info', 3000);
                setIsRemoteAudioEnabled(true);
                // Unmute the audio element
                if (remoteAudioElementRef.current) {
                    remoteAudioElementRef.current.muted = false;
                }
            }
        });

        participant.on('trackDisabled', (track) => {
            // Pass original track (publication) to check trackName
            const isScreenShare = isScreenShareTrack(track);

            if (track.kind === 'video') {
                if (isScreenShare) {

                    showStatusMessage('Other participant paused screen sharing', 'info', 3000);
                    setIsRemoteScreenSharing(false);
                    setIsRemoteScreenSharingLoading(false);
                } else {
                    showStatusMessage('Other participant turned off camera', 'info', 3000);
                    setIsRemoteVideoEnabled(false);
                }
            } else if (track.kind === 'audio') {

                showStatusMessage('Other participant turned off microphone', 'info', 3000);
                setIsRemoteAudioEnabled(false);
                // Mute the audio element
                if (remoteAudioElementRef.current) {
                    remoteAudioElementRef.current.muted = true;
                }
            }
        });
    }, [handleRemoteTrack, handleRemoteTrackRemoved, attachRemoteVideo, showStatusMessage]);

    /**
     * Handle participant disconnected
     */
    const handleParticipantDisconnected = useCallback((participant) => {

        // Show user-friendly message with participant name
        const participantName = participant.identity || 'Someone';
        showStatusMessage(`${participantName} left the meeting`, 'warning', 4000);

        // Remove participant reference
        remoteParticipantsRef.current.delete(participant.sid);

        // Check if there are any other participants
        const hasOtherParticipants = roomRef.current ? roomRef.current.participants.size > 0 : false;
        setHasRemoteParticipant(hasOtherParticipants);

        if (!hasOtherParticipants) {
            setIsRemoteVideoEnabled(false);

            // Detach all remote tracks
            remoteTracksRef.current.forEach(track => {
                track.detach();
                track.removeAllListeners();
            });
            remoteTracksRef.current = [];
        }
    }, [showStatusMessage]);

    /**
     * Initialize speech recognition for transcription
     */
    const initSpeechRecognition = useCallback(() => {
        try {
            // Check if browser supports speech recognition
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

            if (!SpeechRecognition) {

                return null;
            }

            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'en-US';

            recognition.onresult = (event) => {
                let finalTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; i += 1) {
                    const { transcript } = event.results[i][0];
                    if (event.results[i].isFinal) {
                        finalTranscript += `${transcript} `;
                    }
                }

                if (finalTranscript) {
                    const timestamp = new Date().toLocaleTimeString();
                    const newEntry = `[${timestamp}] ${finalTranscript}\n`;
                    transcriptRef.current += newEntry;
                    setTranscript(transcriptRef.current);

                    // Save to localStorage periodically (every 30 seconds)
                    const currentTranscript = transcriptRef.current;
                    if (currentTranscript !== lastSavedTranscriptRef.current) {
                        // Save to localStorage with meetingId as key
                        if (meetingId) {
                            try {
                                const storageKey = `meeting_transcript_${meetingId}`;
                                localStorage.setItem(storageKey, JSON.stringify({
                                    transcript: currentTranscript,
                                    lastUpdated: new Date().toISOString(),
                                    meetingId
                                }));
                                lastSavedTranscriptRef.current = currentTranscript;
                            } catch (error) {
                                console.error('Error saving transcript to localStorage:', error);
                            }
                        }
                    }
                }
            };

            recognition.onerror = (event) => {

                if (event.error === 'no-speech') {
                    // Restart if no speech detected
                    setTimeout(() => {
                        if (recognitionRef.current && isCallActive) {
                            try {
                                recognition.start();
                            } catch (err) {
                                // Error handled silently
                                console.error('Error restarting speech recognition:', err);
                            }
                        }
                    }, 1000);
                }
            };

            recognition.onend = () => {
                // Restart recognition if call is still active
                if (isCallActive && recognitionRef.current) {
                    try {
                        recognition.start();

                    } catch (err) {
                        console.error('Error restarting speech recognition:', err);
                    }
                }
            };

            return recognition;
        } catch (error) {

            return null;
        }
    }, [isCallActive]);

    /**
     * Start transcription
     */
    const startTranscription = useCallback(() => {
        try {

            if (!recognitionRef.current) {
                const recognition = initSpeechRecognition();
                if (recognition) {
                    recognitionRef.current = recognition;
                    recognition.start();

                    showStatusMessage('AI Notetaker started', 'success', 3000);

                    // Load existing transcript from localStorage if available
                    if (meetingId) {
                        try {
                            const storageKey = `meeting_transcript_${meetingId}`;
                            const savedData = localStorage.getItem(storageKey);
                            if (savedData) {
                                const parsed = JSON.parse(savedData);
                                if (parsed.transcript && parsed.transcript.length > 0) {
                                    transcriptRef.current = parsed.transcript;
                                    setTranscript(parsed.transcript);
                                    lastSavedTranscriptRef.current = parsed.transcript;
                                    console.log('✅ Restored transcript from localStorage');
                                }
                            }
                        } catch (error) {
                            console.error('Error loading transcript from localStorage:', error);
                        }
                    }

                    // Set up periodic saving to localStorage (every 30 seconds)
                    if (meetingId && !transcriptSaveIntervalRef.current) {
                        transcriptSaveIntervalRef.current = setInterval(() => {
                            const currentTranscript = transcriptRef.current;
                            if (currentTranscript && currentTranscript.length > 0) {
                                try {
                                    const storageKey = `meeting_transcript_${meetingId}`;
                                    localStorage.setItem(storageKey, JSON.stringify({
                                        transcript: currentTranscript,
                                        lastUpdated: new Date().toISOString(),
                                        meetingId
                                    }));
                                    lastSavedTranscriptRef.current = currentTranscript;
                                    console.log('💾 Transcript auto-saved to localStorage');
                                } catch (error) {
                                    console.error('Error auto-saving transcript:', error);
                                }
                            }
                        }, 30000); // Save every 30 seconds
                    }
                } else {
                    console.error('Failed to initialize speech recognition');
                }
            }
        } catch (error) {
            console.error('Error starting transcription:', error);
        }
    }, [initSpeechRecognition, showStatusMessage, meetingId]);

    /**
     * Stop transcription
     */
    const stopTranscription = useCallback(() => {
        try {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
                recognitionRef.current = null;
            }

            // Clear periodic save interval
            if (transcriptSaveIntervalRef.current) {
                clearInterval(transcriptSaveIntervalRef.current);
                transcriptSaveIntervalRef.current = null;
            }

            // Final save to localStorage before stopping
            if (meetingId && transcriptRef.current) {
                try {
                    const storageKey = `meeting_transcript_${meetingId}`;
                    localStorage.setItem(storageKey, JSON.stringify({
                        transcript: transcriptRef.current,
                        lastUpdated: new Date().toISOString(),
                        meetingId
                    }));
                    console.log('💾 Final transcript save to localStorage');
                } catch (error) {
                    console.error('Error in final transcript save:', error);
                }
            }
        } catch (error) {
            console.error('Error stopping transcription:', error);
        }
    }, [meetingId]);

    /**
     * Get transcript
     */
    const getTranscript = useCallback(() => {
        // Try to get from ref first
        let transcript = transcriptRef.current;

        // If empty, try to load from localStorage
        if (!transcript || transcript.trim().length === 0) {
            if (meetingId) {
                try {
                    const storageKey = `meeting_transcript_${meetingId}`;
                    const savedData = localStorage.getItem(storageKey);
                    if (savedData) {
                        const { transcript: savedTranscript } = JSON.parse(savedData);
                        if (savedTranscript && savedTranscript.length > 0) {
                            transcript = savedTranscript;
                            transcriptRef.current = transcript;
                        }
                    }
                } catch (error) {
                    console.error('Error loading transcript from localStorage:', error);
                }
            }
        }

        return transcript || '';
    }, [meetingId]);

    /**
     * Connect to Twilio Video room
     */
    const connectToRoom = useCallback(async (token, roomName) => {
        try {

            // Validate token
            if (!token || token.length < 50) {
                throw new Error('Invalid token: Token is too short or empty');
            }

            const room = await Video.connect(token, {
                name: roomName,
                audio: true,
                video: { width: 1280, height: 720 },
                bandwidthProfile: {
                    video: {
                        mode: 'collaboration',
                        maxSubscriptionBitrate: 2500000
                    }
                },
                maxAudioBitrate: 16000,
                preferredVideoCodecs: [{ codec: 'VP8', simulcast: true }],
                networkQuality: { local: 1, remote: 1 },
                dominantSpeaker: true
            });

            roomRef.current = room;
            setIsCallActive(true);
            setError(null);
            setReconnectAttempts(0);

            // Publish local tracks
            if (localTracksRef.current.length > 0) {

                localTracksRef.current.forEach(track => {
                    room.localParticipant.publishTrack(track);
                });
            }

            // Attach local video
            attachLocalVideo();

            // Start transcription
            startTranscription();

            // Handle existing participants
            room.participants.forEach(handleParticipantConnected);

            // Handle new participants
            room.on('participantConnected', handleParticipantConnected);
            room.on('participantDisconnected', handleParticipantDisconnected);

            // Handle disconnection
            room.on('disconnected', () => {
                setIsCallActive(false);
                roomRef.current = null;

                if (!isIntentionalEndRef.current) {
                    setIsReconnecting(true);
                    setError('Connection lost. Attempting to reconnect...');

                    // Attempt reconnection
                    if (reconnectTimeoutRef.current) {
                        clearTimeout(reconnectTimeoutRef.current);
                    }

                    reconnectTimeoutRef.current = setTimeout(() => {
                        reconnect();
                    }, 2000);
                }
            });

            // Handle reconnection events
            room.on('reconnecting', () => {
                setIsReconnecting(true);
                setError('Connection unstable. Reconnecting...');
                showStatusMessage('Connection unstable. Reconnecting...', 'warning', 5000);
            });

            room.on('reconnected', () => {

                setIsReconnecting(false);
                setError(null);
                setReconnectAttempts(0);
                showStatusMessage('Connection restored', 'success', 3000);
            });

            return room;
        } catch (err) {

            let errorMessage = 'Failed to connect to video call';

            if (err.code === 53105) {
                errorMessage = 'Network connection failed. Please check your internet connection.';
            } else if (err.code === 53106 || err.code === 53405) {
                errorMessage = 'Authentication failed. Please refresh the page and try again.';
            } else if (err.code === 53107) {
                errorMessage = 'Room not found. Please refresh the page and try again.';
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
            throw err;
        }
    }, [attachLocalVideo, handleParticipantConnected, handleParticipantDisconnected]);

    /**
     * Reconnect function
     */
    const reconnect = useCallback(async () => {
        try {
            // Set reconnect start time if not already set
            if (!reconnectStartTimeRef.current) {
                reconnectStartTimeRef.current = Date.now();
            }

            const elapsedTime = Date.now() - reconnectStartTimeRef.current;

            // If 2 minutes have passed, reload the window
            if (elapsedTime >= maxReconnectDuration) {

                setError('Connection could not be restored. Reloading...');
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
                return;
            }

            setIsReconnecting(true);

            setReconnectAttempts(prev => prev + 1);

            // Wait before attempting reconnection (exponential backoff)
            const delay = Math.min(2000 * (1.5 ** reconnectAttempts), 10000);
            await new Promise(resolve => setTimeout(resolve, delay));

            // Refetch token to get fresh credentials
            const { data: freshTokenResponse } = await refetchToken();
            const freshTokenData = freshTokenResponse?.data?.data;

            if (!freshTokenData?.data?.token) {
                throw new Error('No access token available');
            }

            const { token, roomName } = freshTokenData.data;

            // Attempt to reconnect
            await connectToRoom(token, roomName);

            setReconnectAttempts(0);
            setIsReconnecting(false);
            setError(null);
            reconnectStartTimeRef.current = null; // Reset reconnect start time

        } catch (err) {

            const elapsedTime = Date.now() - reconnectStartTimeRef.current;

            // Check if we've exceeded 2 minutes
            if (elapsedTime >= maxReconnectDuration) {

                setError('Connection could not be restored. Reloading...');
                setIsReconnecting(false);
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
                return;
            }

            setError(`Reconnecting... (${Math.round((maxReconnectDuration - elapsedTime) / 1000)}s remaining)`);

            // Schedule next retry
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }

            reconnectTimeoutRef.current = setTimeout(() => {
                reconnect();
            }, 3000);
        }
    }, [reconnectAttempts, refetchToken, connectToRoom, maxReconnectDuration]);

    /**
     * Start call
     */
    const startCall = useCallback(async () => {
        try {

            setIsConnecting(true);

            if (tokenLoading) {

                return;
            }

            if (tokenError) {
                setError('Failed to get access token. Please refresh and try again.');
                setIsConnecting(false);
                return;
            }

            if (!tokenData?.data?.token) {
                setError('No access token available. Please refresh and try again.');
                setIsConnecting(false);
                return;
            }

            // Initialize local stream
            await initLocalStream();

            const { token, roomName } = tokenData.data;

            // Connect to room
            await connectToRoom(token, roomName);

            // Join socket room
            if (socket) {
                socket.emit('joinVideoRoom', { meetingId, recipientId });
            }

            setIsConnecting(false);
            showStatusMessage('Call connected successfully', 'success', 3000);
        } catch (err) {

            setError(err.message || 'Failed to start call. Please try again.');
            setIsConnecting(false);
            setReconnectAttempts(0);
        }
    }, [socket, meetingId, recipientId, tokenData, tokenLoading, tokenError, initLocalStream, connectToRoom, showStatusMessage]);

    /**
     * End call
     */
    const endCall = useCallback(() => {
        try {

            isIntentionalEndRef.current = true;

            // Clear reconnect timeout and reset reconnect start time
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            reconnectStartTimeRef.current = null;

            // Disconnect from room
            if (roomRef.current) {
                roomRef.current.disconnect();
                roomRef.current = null;
            }

            // Stop and detach local tracks
            localTracksRef.current.forEach(track => {
                track.stop();
                track.detach();
                track.removeAllListeners();
            });
            localTracksRef.current = [];
            cameraTrackRef.current = null;
            audioTrackRef.current = null;
            screenTrackRef.current = null;

            // Detach remote tracks
            remoteTracksRef.current.forEach(track => {
                track.detach();
                track.removeAllListeners();
            });
            remoteTracksRef.current = [];
            remoteParticipantsRef.current.clear();

            // Remove remote audio element
            const remoteAudioElement = document.getElementById('remote-audio');
            if (remoteAudioElement) {
                remoteAudioElement.remove();
            }
            remoteAudioElementRef.current = null;

            // Stop transcription
            stopTranscription();

            // Emit socket events
            if (socket) {
                socket.emit('leaveVideoRoom', { meetingId });
                socket.emit('endCall', { meetingId, recipientId, intentional: true });
            }

            // Reset states
            setIsCallActive(false);
            setLocalStream(null);
            setHasRemoteParticipant(false);
            setIsRemoteVideoEnabled(false);
            setIsRemoteScreenSharing(false);
            setError(null);
            setIsScreenSharing(false);
            setReconnectAttempts(0);
            isIntentionalEndRef.current = false;

            if (onCallEnded) {
                onCallEnded();
            }

        } catch (err) {
            console.error('Error ending call:', err);
        }
    }, [socket, meetingId, recipientId, onCallEnded]);

    /**
     * Toggle audio
     */
    const toggleAudio = useCallback(async () => {
        try {
            if (!audioTrackRef.current) {

                return;
            }

            const audioTrack = audioTrackRef.current;

            if (audioTrack.isEnabled) {

                audioTrack.disable();
                setIsAudioEnabled(false);
                showStatusMessage('Microphone turned off', 'info', 2000);
            } else {

                audioTrack.enable();
                setIsAudioEnabled(true);
                showStatusMessage('Microphone turned on', 'success', 2000);
            }
        } catch (err) {

            setError('Failed to toggle audio. Please try again.');
        }
    }, [showStatusMessage]);

    /**
     * Toggle video
     */
    const toggleVideo = useCallback(async () => {
        try {
            if (!cameraTrackRef.current || isScreenSharing) {

                return;
            }

            const videoTrack = cameraTrackRef.current;

            if (videoTrack.isEnabled) {

                videoTrack.disable();
                setIsVideoEnabled(false);
                showStatusMessage('Camera turned off', 'info', 2000);
            } else {

                videoTrack.enable();
                setIsVideoEnabled(true);
                showStatusMessage('Camera turned on', 'success', 2000);

                // Re-attach video element after enabling
                setTimeout(() => {
                    if (videoTrack.isEnabled && localVideoRef.current) {

                        attachLocalVideo();
                    }
                }, 100);
            }
        } catch (err) {

            setError('Failed to toggle video. Please try again.');
        }
    }, [isScreenSharing, showStatusMessage, attachLocalVideo]);

    /**
     * Start screen sharing
     */
    const startScreenShare = useCallback(async () => {
        try {

            // Check if screen sharing is already active
            if (isScreenSharing) {

                return;
            }

            // Check if room is connected
            if (!roomRef.current) {
                throw new Error('Not connected to room');
            }

            const screenStream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    cursor: 'always',
                    displaySurface: 'monitor',
                    width: { ideal: 1920, max: 1920 },
                    height: { ideal: 1080, max: 1080 },
                    frameRate: { ideal: 15, max: 30 }
                },
                audio: false // Screen share typically doesn't include audio
            });

            const videoTrack = screenStream.getVideoTracks()[0];
            if (!videoTrack) {
                throw new Error('No video track found in screen stream');
            }

            // Create screen share track with proper naming and metadata
            const screenTrack = new Video.LocalVideoTrack(videoTrack, {
                name: 'screen-share',
                logLevel: 'debug'
            });

            // Add custom properties to help with detection
            screenTrack._isScreenShare = true;
            screenTrack._trackType = 'screen-share';

            screenTrackRef.current = screenTrack;

            if (roomRef.current) {
                // Unpublish camera track if it exists
                if (cameraTrackRef.current) {
                    try {
                        await roomRef.current.localParticipant.unpublishTrack(cameraTrackRef.current);

                    } catch (unpublishErr) {
                        // Error handled silently
                    }
                }

                // Publish screen track
                try {
                    const publication = await roomRef.current.localParticipant.publishTrack(screenTrack);

                    // Add event listeners to the publication
                    publication.on('published', () => {

                    });

                    publication.on('unpublished', () => {

                    });

                } catch (publishErr) {

                    throw new Error('Failed to publish screen share track');
                }
            }

            // Add to local tracks
            localTracksRef.current.push(screenTrack);
            setIsScreenSharing(true);
            setIsScreenSharingLoading(true);
            showStatusMessage('Screen sharing started', 'success', 3000);

            // Clear loading state after screen share is established
            setTimeout(() => {
                setIsScreenSharingLoading(false);
            }, 2000);

            // Handle screen share stop events
            screenTrack.once('stopped', () => {

                stopScreenShare();
            });

            // Handle track ended event (when user stops sharing via browser UI)
            videoTrack.addEventListener('ended', () => {

                stopScreenShare();
            });

        } catch (err) {

            if (err.name === 'NotAllowedError') {
                showStatusMessage('Screen sharing permission denied', 'error', 4000);
            } else if (err.name === 'NotFoundError') {
                showStatusMessage('No screen or window available for sharing', 'error', 4000);
            } else if (err.name === 'NotSupportedError') {
                showStatusMessage('Screen sharing not supported in this browser', 'error', 4000);
            } else if (err.name === 'AbortError') {
                showStatusMessage('Screen sharing cancelled', 'info', 2000);
            } else {
                const errorMessage = err.message || 'Failed to start screen sharing. Please try again.';
                setError(errorMessage);
                showStatusMessage(errorMessage, 'error', 4000);
            }
        }
    }, [isScreenSharing, showStatusMessage]);

    /**
     * Stop screen sharing
     */
    const stopScreenShare = useCallback(async () => {
        try {

            if (!screenTrackRef.current) {

                return;
            }

            const screenTrack = screenTrackRef.current;

            if (roomRef.current) {
                try {
                    // Unpublish screen track
                    await roomRef.current.localParticipant.unpublishTrack(screenTrack);

                } catch (unpublishErr) {
                    // Error handled silently
                }

                // Stop and clean up screen track first
                try {
                    screenTrack.stop();
                } catch (stopErr) {
                    // Error handled silently
                }

                // Remove from local tracks
                localTracksRef.current = localTracksRef.current.filter(track => track !== screenTrack);
                screenTrackRef.current = null;

                // Re-enable and re-publish camera track if it exists and video is enabled
                if (cameraTrackRef.current && isVideoEnabled) {
                    try {
                        // Ensure camera track is enabled
                        if (!cameraTrackRef.current.isEnabled) {
                            cameraTrackRef.current.enable();
                        }

                        // Wait a bit for the screen track to be fully unpublished
                        await new Promise(resolve => setTimeout(resolve, 300));

                        // Check if camera track is already published
                        const { localParticipant } = roomRef.current;
                        const existingPublication = Array.from(localParticipant.videoTracks.values())
                            .find(pub => pub.track === cameraTrackRef.current || pub.trackName === cameraTrackRef.current.name);

                        if (!existingPublication) {
                            // Track is not published, publish it
                            await localParticipant.publishTrack(cameraTrackRef.current);
                        } else if (existingPublication.track) {
                            // Track is already published, ensure it's enabled and active
                            if (!existingPublication.track.isEnabled) {
                                existingPublication.track.enable();
                            }
                        }

                        // Re-attach camera video locally
                        setTimeout(() => {
                            attachLocalVideo();
                        }, 200);
                    } catch (publishErr) {
                        // If publish failed, try to unpublish and re-publish
                        try {
                            const { localParticipant } = roomRef.current;
                            const existingPublication = Array.from(localParticipant.videoTracks.values())
                                .find(pub => pub.track === cameraTrackRef.current || pub.trackName === cameraTrackRef.current.name);

                            if (existingPublication) {
                                await localParticipant.unpublishTrack(cameraTrackRef.current);
                                await new Promise(resolve => setTimeout(resolve, 200));
                            }

                            await localParticipant.publishTrack(cameraTrackRef.current);

                            // Re-attach camera video locally
                            setTimeout(() => {
                                attachLocalVideo();
                            }, 200);
                        } catch (retryErr) {
                            // Error handled silently
                            // Try to re-attach anyway
                            setTimeout(() => {
                                attachLocalVideo();
                            }, 200);
                        }
                    }
                }
            } else {
                // If room is not connected, just clean up the screen track
                try {
                    screenTrack.stop();
                } catch (stopErr) {

                    console.error('Error stopping screen track:', stopErr);
                }
                localTracksRef.current = localTracksRef.current.filter(track => track !== screenTrack);
                screenTrackRef.current = null;
            }

            setIsScreenSharing(false);
            setIsScreenSharingLoading(false);
            showStatusMessage('Screen sharing stopped', 'info', 3000);

        } catch (err) {

            setError('Failed to stop screen sharing.');
            showStatusMessage('Error stopping screen share', 'error', 3000);
        }
    }, [isVideoEnabled, attachLocalVideo, showStatusMessage]);

    /**
     * Re-attach local video when track is enabled or when participant state changes
     */
    useEffect(() => {
        if (isVideoEnabled && cameraTrackRef.current && localVideoRef.current && !isScreenSharing) {

            // Small delay to ensure DOM has updated
            setTimeout(() => {
                attachLocalVideo();
            }, 100);
        }
    }, [isVideoEnabled, attachLocalVideo, isScreenSharing, hasRemoteParticipant]);

    /**
     * Re-attach remote screen share track when video element becomes available or state changes
     */
    useEffect(() => {
        if (isRemoteScreenSharing && remoteScreenTrackRef.current && remoteVideoRef.current) {
            // Ensure screen share track is attached to the video element
            const actualTrack = remoteScreenTrackRef.current;
            const remoteVideoElement = remoteVideoRef.current;

            // Check if track is already attached
            if (actualTrack && remoteVideoElement) {
                // Small delay to ensure DOM is ready
                setTimeout(() => {
                    try {
                        // Check if track is already attached to this element
                        const isAlreadyAttached = remoteVideoElement.srcObject !== null ||
                            (actualTrack.mediaStreamTrack &&
                                remoteVideoElement.srcObject?.getTracks().includes(actualTrack.mediaStreamTrack));

                        if (!isAlreadyAttached) {
                            // Detach first to avoid duplicates
                            actualTrack.detach();

                            // Ensure track is enabled
                            if (!actualTrack.isEnabled) {
                                actualTrack.enable();
                            }

                            // Attach the track
                            actualTrack.attach(remoteVideoElement);
                        }

                        // Ensure video element is visible and configured
                        remoteVideoElement.style.display = 'block';
                        remoteVideoElement.style.visibility = 'visible';
                        remoteVideoElement.style.opacity = '1';
                        remoteVideoElement.setAttribute('autoplay', 'true');
                        remoteVideoElement.setAttribute('playsinline', 'true');

                        // Force play
                        remoteVideoElement.load();
                        requestAnimationFrame(() => {
                            remoteVideoElement.play().catch(() => {
                                // Error handled silently
                            });
                        });
                    } catch (err) {
                        // Error handled silently
                    }
                }, 100);
            }
        }
    }, [isRemoteScreenSharing, remoteVideoRef]);

    /**
     * Cleanup on unmount
     */
    useEffect(() => () => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }

        if (statusMessageTimeoutRef.current) {
            clearTimeout(statusMessageTimeoutRef.current);
        }

        // Clear transcript save interval
        if (transcriptSaveIntervalRef.current) {
            clearInterval(transcriptSaveIntervalRef.current);
            transcriptSaveIntervalRef.current = null;
        }

        localTracksRef.current.forEach(track => {
            track.stop();
            track.detach();
            track.removeAllListeners();
        });
        localTracksRef.current = [];
        cameraTrackRef.current = null;
        audioTrackRef.current = null;
        screenTrackRef.current = null;

        remoteTracksRef.current.forEach(track => {
            track.detach();
            track.removeAllListeners();
        });
        remoteTracksRef.current = [];
        remoteScreenTrackRef.current = null;

        if (roomRef.current) {
            roomRef.current.disconnect();
        }

        const remoteAudioElement = document.getElementById('remote-audio');
        if (remoteAudioElement) {
            remoteAudioElement.remove();
        }
        remoteAudioElementRef.current = null;
    }, []);

    // Debug function to test screen share detection
    const debugScreenShare = useCallback(() => {
        if (roomRef.current) {
            roomRef.current.participants.forEach(() => {
                // Debug function placeholder
            });
        }
    }, [isScreenSharing, isRemoteScreenSharing]);

    return {
        localStream,
        isCallActive,
        error,
        isReconnecting,
        isConnecting,
        isAudioEnabled,
        isVideoEnabled,
        isRemoteVideoEnabled,
        isRemoteAudioEnabled,
        hasRemoteParticipant,
        isScreenSharing,
        isRemoteScreenSharing,
        isScreenSharingLoading,
        isRemoteScreenSharingLoading,
        tokenLoading,
        tokenError,
        reconnectAttempts,
        statusMessage,
        statusMessageType,
        transcript,
        startCall,
        endCall,
        reconnect,
        toggleAudio,
        toggleVideo,
        startScreenShare,
        stopScreenShare,
        getTranscript,
        localVideoRef,
        remoteVideoRef,
        debugScreenShare
    };
};

export default useTwilioVideo;